import { GroupIndicatorChart, SingleIndicatorChart } from 'src/app/shared/interface/company.interface';
import { SelectOptionData } from 'src/app/shared/interface/profet-control-widget.interface';
import { PropertyAttribute } from '../../property/interface/property.interface';
import { InsightKeyIndicatorConfig, NeighborhoodInsightChartConfig } from '../interface/neighborhood.interface';
import {
  ApproachTypeConfig,
  CompSearchSubFilter,
  CompSearchType,
  NotificationStatus,
  OrderRoutePathConfig,
  PrimarySearchFilter,
  PropertyType,
  StatusType,
  TimeFrameType,
  ValuationType,
  ValueAdjustmentDataSourceType,
} from '../interface/order.interface';
import { RouteTreeNode } from '../service/route-tree.service';

export class OrderConfig {
  /**
   * Default values to be used for searching comparables while creating a new order.
   */
  static readonly defaultPrimarySearchFilter: PrimarySearchFilter = {
    bathsRange: {
      from: null,
      to: null,
      tolerance: null,
      source: null,
    },
    bedsRange: {
      from: null,
      to: null,
      tolerance: null,
      source: null,
    },
    halfBathsRange: {
      from: null,
      to: null,
      tolerance: null,
      source: null,
    },
    fullBathsRange: {
      from: null,
      to: null,
      tolerance: null,
      source: null,
    },
    effectiveDate: null,
    includePublicRecordSales: true,
    limitToSubjectZip: true,
    listDateRange: {
      from: null,
      to: null,
    },
    livingAreaRange: {
      from: null,
      to: null,
      tolerance: null,
      source: null,
    },
    lotSizeRange: {
      from: null,
      to: null,
      tolerance: null,
      source: null,
    },
    monthsBack: 12,
    offMarketStatusList: null,
    polygonList: null,
    priceRange: {
      from: null,
      to: null,
    },
    propertyTypeList: null,
    radius: 2,
    saleType: null,
    schoolDistrict: null,
    searchMode: CompSearchType.radius,
    soldDateRange: {
      from: null,
      to: null,
    },
    statusList: [StatusType.active, StatusType.sold, StatusType.pending],
    storiesRange: {
      from: null,
      to: null,
      tolerance: null,
      source: null,
    },
    timeframeOption: TimeFrameType.effectiveDate,
    valuationType: ValuationType.SALES,
    yearBuiltRange: {
      from: null,
      to: null,
      tolerance: null,
      source: null,
    },
  };

  static readonly defaultCompSearchSubFilter: CompSearchSubFilter = {
    includeHOA: null,
    includeSolarPower: null,
    includeGuestHouse: null,
    includeFireplace: null,
    includeGarage: null,
    subDivisionName: '',
    viewFactor: null,
    garageType: null,
    poolType: null,
    basementType: null,
    basementFinishedAreaRange: { from: 0, to: 0 },
    basementFinishedTotalAreaRange: { from: 0, to: 0 },
    address: '',
    city: '',
    state: '',
    zipCode: '',
    propertyType: PropertyType.singleFamily,
    status: null,
    bedsRange: { from: 0, to: 0 },
    bathsRange: { from: 0, to: 0 },
    livingAreaRange: { from: 0, to: 0 },
    listPriceRange: { from: 0, to: 0 },
    soldPriceRange: { from: 0, to: 0 },
    storiesRange: { from: 0, to: 0 },
    similarityScoreRange: { from: 0, to: 0 },
  };

  static readonly viewOptionList: SelectOptionData[] = [
    {
      key: null,
      displayValue: 'Any View',
    },
    {
      key: 'waterFront',
      displayValue: 'Water Front',
    },
    {
      key: 'woods',
      displayValue: 'Woods',
    },
    {
      key: 'park',
      displayValue: 'Park',
    },
    {
      key: 'pastoral',
      displayValue: 'Pastoral',
    },
    {
      key: 'golfCourse',
      displayValue: 'Golf Course',
    },
    {
      key: 'citySkyline',
      displayValue: 'City Sky Line',
    },
    {
      key: 'mountain',
      displayValue: 'Mountain',
    },
    {
      key: 'residential',
      displayValue: 'Residential',
    },
    {
      key: 'cityStreet',
      displayValue: 'City Street',
    },
    {
      key: 'industrial',
      displayValue: 'Industrial',
    },
    {
      key: 'powerLines',
      displayValue: 'Power Lines',
    },
    {
      key: 'limitedSight',
      displayValue: 'Limited Sight',
    },
  ];

  static readonly garageOptionList: SelectOptionData[] = [
    {
      key: null,
      displayValue: 'Any Garage',
    },
    {
      key: 'attached',
      displayValue: 'Attached',
    },
    {
      key: 'builtIn',
      displayValue: 'Built-In',
    },
    {
      key: 'carPort',
      displayValue: 'Car Port',
    },
    {
      key: 'detached',
      displayValue: 'Detached',
    },
    {
      key: 'unknown',
      displayValue: 'Unknown',
    },
  ];

  static readonly poolOptionList: SelectOptionData[] = [
    {
      key: null,
      displayValue: 'Any Pool',
    },
    {
      key: 'inGround',
      displayValue: 'In Ground',
    },
    {
      key: 'aboveGround',
      displayValue: 'Above Ground',
    },
    {
      key: 'unknown',
      displayValue: 'Unknown',
    },
  ];

  static readonly basementOptionList: SelectOptionData[] = [
    {
      key: null,
      displayValue: 'Any Basement',
    },
    {
      key: 'crawlSpace',
      displayValue: 'Crawl Space',
    },
    {
      key: 'fullBasement',
      displayValue: 'Full Basement',
    },
    {
      key: 'partialBasement',
      displayValue: 'Partial Basement',
    },
    {
      key: 'unknown',
      displayValue: 'Unknown',
    },
  ];

  /**
   * Default value set to NeighborhoodInsights state.
   */
  // static readonly defaultNeighborhoodInsight: NeighborhoodInsight = {
  //   zipCodeInsight: {
  //     data: [],
  //     selectedPropertyType: [],
  //     selectedChart: [],
  //   },
  //   comparableInsight: {
  //     data: [],
  //     selectedPropertyType: [],
  //     selectedChart: [],
  //   },
  // };

  /**
   * Neighborhood insight chart configuration used by Zip-Code Insights charts and Comparable Insights charts.
   */
  static readonly neighborhoodInsightChartConfig: NeighborhoodInsightChartConfig[] = [
    {
      key: GroupIndicatorChart.absorptionRate,
      displayValue: 'Absorption Rate',
      propertyType: 'subject',
      dataType: 'number',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'MonthsOfSupply',
          label: 'Months of Supply',
        },
        {
          chartType: 'bar',
          dataKey: 'ForSaleCount',
          label: 'Count of Listings',
        },
        {
          chartType: 'bar',
          dataKey: 'SoldCount',
          label: 'Count of Contracts',
        },
      ],
    },
    {
      key: GroupIndicatorChart.medianDOM,
      displayValue: 'Median Days on Market',
      propertyType: 'subject',
      dataType: 'number',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'AvgDOM',
          label: 'Median Days on Market',
        },
      ],
    },
    {
      key: GroupIndicatorChart.priceTrend,
      displayValue: 'Price Trends',
      propertyType: 'subject',
      dataType: 'currency',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'MedianSoldPricePerSqft',
          label: 'Median SP/sq.ft.',
        },
        {
          chartType: 'bar',
          dataKey: 'MedianListPrice',
          label: 'Median List Price',
        },
        {
          chartType: 'bar',
          dataKey: 'MedianSoldPrice',
          label: 'Median Sale Price',
        },
      ],
    },
    {
      key: GroupIndicatorChart.medianSoldToListPrice,
      displayValue: 'Median Sold-Price-to-List-Price %',
      propertyType: 'subject',
      dataType: 'number',
      dataset: [
        {
          chartType: 'bar',
          dataKey: 'MedianSoldToListPricePercent',
          label: 'Median Sold-Price-to-List-Price %',
        },
      ],
    },
    {
      key: SingleIndicatorChart.medianListPrice,
      displayValue: 'Median List Price',
      propertyType: 'all',
      dataType: 'currency',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'MedianListPrice',
          label: 'Median List Price',
        },
      ],
    },
    {
      key: SingleIndicatorChart.avgDOM,
      displayValue: 'Average Days on Market',
      propertyType: 'all',
      dataType: 'number',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'AvgDOM',
          label: 'Average Days on Market',
        },
      ],
    },
    {
      key: SingleIndicatorChart.medianSoldPricePerSqft,
      displayValue: 'Median Sold Price per Sqft',
      propertyType: 'all',
      dataType: 'currency',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'MedianSoldPricePerSqft',
          label: 'Median Sold Price per Sqft',
        },
      ],
    },
    {
      key: SingleIndicatorChart.inventory,
      displayValue: 'Inventory',
      propertyType: 'all',
      dataType: 'number',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'NewListingCount',
          label: 'Inventory',
        },
      ],
    },
    {
      key: GroupIndicatorChart.rentalIncome,
      displayValue: 'Rental Income',
      propertyType: 'subject',
      dataType: 'currency',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'rentalIncome',
          label: 'Rental Income',
        },
      ],
    },
    {
      key: GroupIndicatorChart.nightPrice,
      displayValue: 'Night Price',
      propertyType: 'subject',
      dataType: 'currency',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'nightPrice',
          label: 'Night Price',
        },
      ],
    },
    {
      key: GroupIndicatorChart.occupancy,
      displayValue: 'Occupancy',
      propertyType: 'subject',
      dataType: 'number',
      dataset: [
        {
          chartType: 'line',
          dataKey: 'occupancy',
          label: 'Occupancy',
        },
      ],
    },
  ];

  static readonly chartPropertyTypeConfig = {
    all: {
      dataKey: 'Residential',
      displayValue: 'All',
    },
    singleFamilyHomes: {
      dataKey: 'SingleFamily',
      displayValue: 'Single Family',
    },
    condos: {
      dataKey: 'Condominium',
      displayValue: 'Condominium',
    },
    townHomes: {
      dataKey: 'TownHouse',
      displayValue: 'TownHouse',
    },
  };

  static readonly neighborhoodInsightKeyIndicatorConfig: InsightKeyIndicatorConfig[] = [
    {
      displayValue: 'Median List Price',
      dataKey: 'MedianListPrice',
      dataType: 'currency',
    },
    {
      displayValue: 'Asking Price/sq ft',
      dataKey: 'MedianListPricePerSqft',
      dataType: 'currency',
    },
    {
      displayValue: 'Average Days on Market',
      dataKey: 'AvgDOM',
      dataType: 'number',
    },
    {
      displayValue: 'New Listings',
      dataKey: 'NewListingCount',
      dataType: 'number',
    },
    {
      displayValue: 'Total Sold',
      dataKey: 'SoldCount',
      dataType: 'number',
    },
    {
      displayValue: 'Median House Size(sq ft)',
      dataKey: 'MedianGLA',
      dataType: 'number',
    },
    {
      displayValue: 'Median Bedrooms Count',
      dataKey: 'MedianBed',
      dataType: 'number',
    },
    {
      displayValue: 'Median Bathrooms Count',
      dataKey: 'MedianBath',
      dataType: 'number',
    },
  ];

  static defaultOrderRoutePathList: OrderRoutePathConfig[] = [
    {
      route: 'basic-info',
      displayValue: 'Order Summary',
      jsonPath: 'orderSummary',
      companySettingJsonPath: 'orderSummary.enabled',
      children: [],
      validationGroup: [],
      validationLabel: 'Order Summary',
      validationRoute: 'basic-info',
    },
    {
      route: 'subject',
      displayValue: 'Subject Property',
      jsonPath: 'subject',
      companySettingJsonPath: 'subject.enabled',
      validationGroup: [],
      validationLabel: 'Subject Property',
      validationRoute: 'subject',

      children: [
        {
          route: 'detail',
          displayValue: 'Detail',
          jsonPath: '',
          companySettingJsonPath: 'subject.enabled',
          validationGroup: [],
          validationLabel: 'Subject > Detail',
          validationRoute: 'subject/detail',
          children: [],
        },
        {
          route: 'image',
          displayValue: 'Images',
          jsonPath: '',
          companySettingJsonPath: 'image.enabled',
          validationGroup: [],
          validationLabel: 'Subject > Images',
          validationRoute: 'subject/image',
          children: [],
        },
        {
          route: 'flood',
          displayValue: 'Flood',
          jsonPath: '',
          companySettingJsonPath: 'flood.enabled',
          validationGroup: [],
          validationLabel: ' Subject > Flood',
          validationRoute: 'subject/flood',
          children: [],
        },
        {
          route: 'plat',
          displayValue: 'Plat',
          jsonPath: '',
          companySettingJsonPath: 'plat.enabled',
          validationGroup: [],
          validationLabel: 'Subject > Plat',
          validationRoute: 'subject/plat',
          children: [],
        },
        {
          route: 'sketch',
          displayValue: 'Sketch',
          jsonPath: '',
          companySettingJsonPath: 'sketch.enabled',
          validationGroup: [],
          validationLabel: 'Subject > Sketch',
          validationRoute: 'subject/sketch',
          children: [],
        },
        {
          route: 'project',
          displayValue: 'Project',
          jsonPath: '',
          companySettingJsonPath: 'project.enabled',
          validationGroup: [],
          validationLabel: 'Subject > Project',
          validationRoute: 'subject/project',
          children: [],
        },
        {
          route: 'property-condition',
          displayValue: 'PCR',
          jsonPath: '',
          companySettingJsonPath: 'propertyCondition.enabled',
          validationGroup: [],
          validationLabel: 'Subject > PCR',
          validationRoute: 'subject/property-condition',
          children: [],
        },
      ],
    },
    {
      route: 'sales-approach',
      displayValue: 'Sales Comparison',
      jsonPath: 'salesComparison',
      companySettingJsonPath: 'salesComparison.enabled',
      validationGroup: [],
      validationLabel: 'Sales Comparison',
      validationRoute: 'sales-approach',

      children: [
        {
          route: 'selection',
          displayValue: 'Comps',
          jsonPath: 'selection',
          companySettingJsonPath: 'salesComparison.selection.enabled',
          validationGroup: [],
          validationLabel: 'Sales Comparison > Comps',
          validationRoute: 'sales-approach/selection',
          children: [],
        },
        {
          route: 'adjustment',
          displayValue: 'Grid',
          jsonPath: 'adjustment',
          companySettingJsonPath: 'salesComparison.adjustment.enabled',
          validationGroup: ['subject/detail', 'sales-approach/adjustment'],
          validationLabel: 'Sales Comparison > Grid',
          validationRoute: 'sales-approach/adjustment',
          children: [],
        },
        {
          route: 'comp-map',
          displayValue: 'Comp Map',
          jsonPath: 'map',
          companySettingJsonPath: 'salesComparison.compMap.enabled',
          validationGroup: [],
          validationLabel: 'Sales Comparison > Comp Map',
          validationRoute: 'sales-approach/comp-map',
          children: [],
        },
        {
          route: 'comment',
          displayValue: 'Comments',
          jsonPath: 'comments',
          companySettingJsonPath: 'salesComparison.comments.enabled',
          validationGroup: [],
          validationLabel: 'Sales Comparison > Comments',
          validationRoute: 'sales-approach/comment',
          children: [],
          condition: { route: 'salesComparison.comments.gridCommentSheet', value: false },
        },
        {
          route: 'priorSale',
          displayValue: 'Prior Sales',
          jsonPath: 'priorSale',
          companySettingJsonPath: 'salesComparison.priorSale.enabled',
          validationGroup: [],
          validationLabel: 'Sales Comparison > Prior Sales',
          validationRoute: 'sales-approach/priorSale',
          children: [],
        },
      ],
    },
    {
      route: 'cost-approach',
      displayValue: 'Cost Approach',
      jsonPath: 'costApproach',
      companySettingJsonPath: 'costApproach.enabled',
      validationGroup: [],
      validationLabel: 'Cost Approach',
      validationRoute: 'cost-approach',

      children: [
        {
          route: 'building',
          displayValue: 'Bldg',
          jsonPath: 'costOfBuildingDetail',
          companySettingJsonPath: 'costApproach.building.enabled',
          validationGroup: [],
          validationLabel: 'Cost Approach > Bldg',
          validationRoute: 'cost-approach/building',
          children: [],
        },
        {
          route: 'land',
          displayValue: 'Land',
          jsonPath: 'costOfLandSaleDetail',
          companySettingJsonPath: 'costApproach.land.enabled',
          validationGroup: [],
          validationLabel: 'Cost Approach > Land',
          validationRoute: 'cost-approach/land',
          children: [],
        },
      ],
    },
    {
      route: 'short-term-rental',
      displayValue: 'Short Term Rent',
      jsonPath: 'shortTermRentalReport',
      companySettingJsonPath: 'shortTermRental.enabled',
      validationGroup: [],
      validationLabel: 'Short Term Rent',
      validationRoute: 'short-term-rental',

      children: [
        {
          route: 'selection',
          displayValue: 'Comps',
          jsonPath: 'selection',
          companySettingJsonPath: 'shortTermRental.selection.enabled',
          validationGroup: [],
          validationLabel: 'Short Term Rent > Comps',
          validationRoute: 'short-term-rental/selection',
          children: [],
        },
        {
          route: 'adjustment',
          displayValue: 'Grid',
          jsonPath: 'adjustment',
          companySettingJsonPath: 'shortTermRental.adjustment.enabled',
          validationGroup: ['subject/detail', 'short-term-rental/adjustment'],
          validationLabel: 'Short Term Rent > Grid',
          validationRoute: 'short-term-rental/adjustment',
          children: [],
        },
        {
          route: 'income',
          displayValue: 'Income',
          jsonPath: 'income',
          companySettingJsonPath: 'shortTermRental.income.enabled',
          validationGroup: [],
          validationLabel: 'Short Term Rent > Income',
          validationRoute: 'short-term-rental/income',
          children: [],
        },
        {
          route: 'value',
          displayValue: 'Value',
          jsonPath: 'value',
          companySettingJsonPath: 'shortTermRental.value.enabled',
          validationGroup: [],
          validationLabel: ' Long Term Rent > Value',
          validationRoute: 'short-term-rental/value',
          children: [],
        },
      ],
    },
    {
      route: 'long-term-rental',
      displayValue: 'Long Term Rent',
      jsonPath: '',
      companySettingJsonPath: 'longTermRental.enabled',
      validationGroup: [],
      validationLabel: 'Long Term Rent',
      validationRoute: 'long-term-rental',
      children: [
        {
          route: 'selection',
          displayValue: 'LT Comps',
          jsonPath: '',
          companySettingJsonPath: 'longTermRental.selection.enabled',
          validationGroup: [],
          validationLabel: 'Long Term Rent > Comps',
          validationRoute: 'long-term-rental/selection',
          children: [],
        },
        {
          route: 'adjustment',
          displayValue: 'LT Grid',
          jsonPath: '',
          companySettingJsonPath: 'longTermRental.adjustment.enabled',
          validationGroup: ['subject/detail', 'long-term-rental/adjustment'],
          validationLabel: 'Long Term Rent > Grid',
          validationRoute: 'long-term-rental/adjustment',
          children: [],
        },
        {
          route: 'income',
          displayValue: 'LT Income',
          jsonPath: '',
          companySettingJsonPath: 'longTermRental.income.enabled',
          validationGroup: [],
          validationLabel: 'Long Term Rent > Income',
          validationRoute: 'long-term-rental/income',
          children: [],
        },
        {
          route: 'value',
          displayValue: 'Value',
          jsonPath: 'value',
          companySettingJsonPath: 'longTermRental.value.enabled',
          validationGroup: [],
          validationLabel: 'Long Term Rent > Value',
          validationRoute: 'long-term-rental/value',
          children: [],
        },
      ],
    },

    {
      route: 'neighborhood',
      displayValue: 'Neighborhood',
      jsonPath: 'neighborhood',
      companySettingJsonPath: 'neighborhood.enabled',
      validationGroup: [],
      validationLabel: 'Neighborhood',
      validationRoute: 'neighborhood',
      children: [
        {
          route: 'locality',
          displayValue: 'Locality',
          jsonPath: 'locality',
          companySettingJsonPath: 'neighborhood.locality.enabled',
          validationGroup: [],
          validationLabel: 'Neighborhood > Inventory',
          validationRoute: 'neighborhood/locality',
          children: [],
        },
        {
          route: 'inventory',
          displayValue: 'Inventory',
          jsonPath: 'inventory',
          companySettingJsonPath: 'neighborhood.inventory.enabled',
          validationGroup: [],
          validationLabel: 'Neighborhood > Inventory',
          validationRoute: 'neighborhood/inventory',
          children: [],
        },
        {
          route: 'trends',
          displayValue: 'Trends',
          jsonPath: 'trends',
          companySettingJsonPath: 'neighborhood.trends.enabled',
          validationGroup: [],
          validationLabel: 'Neighborhood > Trends',
          validationRoute: 'neighborhood/trends',
          children: [],
        },
      ],
    },
    {
      route: 'project-info',
      displayValue: 'Project Information',
      jsonPath: 'projectInformation',
      companySettingJsonPath: 'projectInformation.enabled',
      children: [],
      validationGroup: [],
      validationLabel: 'Project Information',
      validationRoute: 'project-info',
    },
    {
      route: 'value-reconciliation',
      displayValue: 'Value Reconciliation',
      jsonPath: 'valueReconciliation',
      companySettingJsonPath: 'reconciliation.enabled',
      children: [],
      validationGroup: [],
      validationLabel: 'Value Reconciliation',
      validationRoute: 'value-reconciliation',
    },
    {
      route: 'additionalDocs',
      displayValue: 'Docs and Comments',
      jsonPath: 'additionalFiles',
      companySettingJsonPath: ['additionalDocs.enabled', 'additionalCommentsConfig.enabled'],
      children: [],
      validationGroup: [],
      validationLabel: 'Docs and Comments',
      validationRoute: 'additionalDocs',
    },
    {
      route: 'certification',
      displayValue: 'Certification',
      jsonPath: 'certification',
      companySettingJsonPath: 'certificationPage.enabled',
      children: [],
      validationGroup: ['all'],
      validationLabel: 'Certification',
      validationRoute: 'certification',
    },
    {
      route: 'preview',
      displayValue: 'Preview',
      jsonPath: 'preview',
      companySettingJsonPath: 'preview.enabled',
      children: [],
      validationGroup: ['all'],
      validationLabel: 'Preview',
      validationRoute: 'preview',
    },
  ];

  static orderRoutePathList: OrderRoutePathConfig[] = [];

  static comparableSortConfig: { [key in string]: string } = {
    BathroomsDecimal: 'bathroomsTotal',
  };

  static notificationConfig: { [key in string]: { title: string; status: { [key in NotificationStatus]: string } } } = {
    inspection: {
      title: 'Inspection',
      status: {
        INVALID: 'Provided Inspection ID is invalid.',
        SUCCESS: 'Inspection Data successfully fetched.',
        FAILED: 'Failed to fetch inspection details.',
        PENDING: 'Fetching the inspection data is in progress.',
      },
    },
  };

  static validationNotAppliedRoutes: string[] = ['comps-filter'];

  static approachTypeConfig: ApproachTypeConfig = {
    salesComparison: { valuationType: ValuationType.SALES, page: 'sales-approach', displayValue: 'Sales Comparison' },
    costApproach: { valuationType: ValuationType.COST, page: 'cost-approach', displayValue: 'Cost Approach' },
    shortTermRental: {
      valuationType: ValuationType.SHORT_TERM_RENTAL,
      page: 'short-term-rental',
      displayValue: 'Short Term Rental',
    },
    longTermRental: {
      valuationType: ValuationType.LONG_TERM_RENTAL,
      page: 'long-term-rental',
      displayValue: 'Long Term Rental',
    },
  };

  static routeTreeConfig: RouteTreeNode<OrderRoutePathConfig>;

  static adjustmentModels: Array<ValueAdjustmentDataSourceType> = [
    'propmixAdjustment',
    'pairedSalesAdjustment',
    'regressionAdjustment',
    'multiVariableRegressionAdjustment',
    'unitAdjustment',
  ];

  static multiVariableRegressionVariables: PropertyAttribute[] = [
    'BedroomsTotal',
    'bathroomsTotal',
    'LivingArea',
    'LotSizeSquareFeet',
  ];
}
